import { useContext, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { Col, Glyphicon, Row } from 'react-bootstrap';
import Loader from 'components/loader/Loader';
import useFormatCurrency from 'hooks/useFormatCurrency';
import { useTranslation } from 'react-i18next';
import { LeadScreenAndProtectStatus } from 'models/ScreenAndProtect';
import { isNonEmptyString } from 'utils/typeGuards';
import LeadModalContext from '../../LeadModalContext';
import useLeadScreenAndProtectDetails from '../../useLeadScreenAndProtectDetails';
import {
  ActionButton,
  BookingStatus,
  Section,
  StyledRow,
} from './LeadModalScreenAndProtectTab.styles';

const getStatusIcon = (status: LeadScreenAndProtectStatus) => {
  if (status === LeadScreenAndProtectStatus.APPROVED) {
    return <Glyphicon glyph="ok-sign" />;
  }

  if (
    status === LeadScreenAndProtectStatus.FLAGGED ||
    status === LeadScreenAndProtectStatus.FAILED
  ) {
    return <Glyphicon glyph="warning-sign" />;
  }

  if (status === LeadScreenAndProtectStatus.PENDING) {
    return <Glyphicon glyph="time" />;
  }

  if (
    status === LeadScreenAndProtectStatus.CANCELLED ||
    status === LeadScreenAndProtectStatus.REJECTED
  ) {
    return <Glyphicon glyph="remove-sign" />;
  }

  return <Glyphicon glyph="remove-sign" />;
};

const LeadModalScreenAndProtectTab = () => {
  const { lead } = useContext(LeadModalContext);
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();
  const { screenAndProtectDetails, isLoading } = useLeadScreenAndProtectDetails(
    lead?.uid,
  );

  const shouldShowIncidentResolution = useMemo(() => {
    return (
      (screenAndProtectDetails?.status ===
        LeadScreenAndProtectStatus.APPROVED ||
        screenAndProtectDetails?.status ===
          LeadScreenAndProtectStatus.FLAGGED) &&
      (!!screenAndProtectDetails?.expirationDate ||
        !!screenAndProtectDetails?.requestUrl)
    );
  }, [screenAndProtectDetails]);

  if (isLoading) {
    return <Loader />;
  }

  if (!screenAndProtectDetails) {
    return null;
  }

  const handleFileRequest = () => {
    if (!screenAndProtectDetails.requestUrl) {
      return;
    }

    window.open(
      screenAndProtectDetails.requestUrl,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <Row>
      <Col xs={12} md={6}>
        <Section>
          <h3>
            {t('componentLead.modal.screenAndProtectTab.protectionDetails')}
          </h3>

          <StyledRow>
            <Col xs={6}>
              <span>
                {t('componentLead.modal.screenAndProtectTab.bookingStatus')}
              </span>
            </Col>
            <Col xs={6}>
              <BookingStatus
                $status={screenAndProtectDetails.status}
                data-testid="screen-and-protect-booking-status-value"
              >
                {getStatusIcon(screenAndProtectDetails.status)}
                {t('componentLead.modal.screenAndProtectTab.status', {
                  context: screenAndProtectDetails.status,
                })}
              </BookingStatus>
            </Col>
          </StyledRow>

          {(screenAndProtectDetails.status ===
            LeadScreenAndProtectStatus.FLAGGED ||
            screenAndProtectDetails.status ===
              LeadScreenAndProtectStatus.FAILED) &&
            isNonEmptyString(screenAndProtectDetails.reason) && (
              <StyledRow>
                <Col xs={6}>
                  <span>
                    {t('componentLead.modal.screenAndProtectTab.statusDetails')}
                  </span>
                </Col>
                <Col xs={6}>
                  <span data-testid="screen-and-protect-protection-reason-value">
                    {screenAndProtectDetails.reason}
                  </span>
                </Col>
              </StyledRow>
            )}

          <StyledRow>
            <Col xs={6}>
              <span>
                {t('componentLead.modal.screenAndProtectTab.protectionAmount')}
              </span>
            </Col>
            <Col xs={6}>
              <span data-testid="screen-and-protect-protection-amount-value">
                {formatCurrency({
                  value: screenAndProtectDetails.protectionAmount,
                  currency: 'USD',
                })}
              </span>
            </Col>
          </StyledRow>
        </Section>

        {shouldShowIncidentResolution && (
          <Section>
            <h3>
              {t('componentLead.modal.screenAndProtectTab.incidentResolution')}
            </h3>

            <StyledRow>
              <Col xs={6}>
                <span>
                  {t(
                    'componentLead.modal.screenAndProtectTab.fileIncidentUntil',
                  )}
                </span>
              </Col>
              <Col xs={6}>
                <span data-testid="screen-and-protect-expiration-date-value">
                  {format(
                    parseISO(screenAndProtectDetails.expirationDate),
                    'MMM d, yyyy',
                  )}
                </span>
              </Col>
            </StyledRow>

            {screenAndProtectDetails.requestUrl && (
              <ActionButton
                $colorScheme="green"
                data-testid="screen-and-protect-file-request-button"
                onClick={handleFileRequest}
              >
                {t('componentLead.modal.screenAndProtectTab.fileIncident')}
              </ActionButton>
            )}
          </Section>
        )}
      </Col>
    </Row>
  );
};

export default LeadModalScreenAndProtectTab;
